import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type NavigateParams = {
  profileId?: string | null;
  ticker?: string | null;
};

type Target = 'dashboard' | 'canvas';

const buildProfilePath = (
  { profileId, ticker }: NavigateParams,
  target: Target
): string | null => {
  const idOrTicker = (profileId ?? ticker ?? '').trim();
  if (!idOrTicker) return null;
  if (target === 'dashboard') {
    return `/profiles/${encodeURIComponent(idOrTicker)}`;
  }
  // canvas fallback
  if (profileId && profileId.trim().length > 0) {
    return `/canvas?profileId=${encodeURIComponent(profileId.trim())}`;
  }
  if (ticker && ticker.trim().length > 0) {
    return `/canvas?ticker=${encodeURIComponent(ticker.trim())}`;
  }
  return null;
};

export const useProfileNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCanvasContext = React.useMemo(() => location.pathname.startsWith('/canvas'), [location.pathname]);

  const openProfile = React.useCallback(
    (params: NavigateParams, opts?: { target?: Target }) => {
      const target = opts?.target ?? (isCanvasContext ? 'canvas' : 'dashboard');
      const path = buildProfilePath(params, target);
      if (!path) return false;
      navigate(path);
      return true;
    },
    [navigate, isCanvasContext]
  );

  const openProfileById = React.useCallback(
    (profileId: string, opts?: { target?: Target }) => openProfile({ profileId }, opts),
    [openProfile]
  );

  const openProfileByTicker = React.useCallback(
    (ticker: string, opts?: { target?: Target }) => openProfile({ ticker }, opts),
    [openProfile]
  );

  return {
    openProfile,
    openProfileById,
    openProfileByTicker,
  } as const;
};

export type UseProfileNavigation = ReturnType<typeof useProfileNavigation>;
